import { useEditorState } from "@tiptap/react";
import {
  DOCUMENT_TYPES,
  FILE_TYPE,
  IMAGE_TYPES,
  VIDEO_TYPES,
} from "Constant/constants";
import { uploadHandler } from "Utils";

const COLORS = [
  "#000000",
  "#4A4A4A",
  "#7E7E7E",
  "#B0B0B0",
  "#D3D3D3",
  "#E8E8E8",
  "#FFFFFF",
  "#D0342C",
  "#FF6B33",
  "#FFCC00",
  "#2AD4C1",
  "#2A6FD4",
  "#7B2AD4",
  "#D42A86",
  "#F3B2AE",
  "#F3CEAE",
  "#EEF3AE",
  "#AEF3CF",
  "#AEC7F3",
  "#C9AEF3",
];

export default function Toolbar({
  editor,
  moduleType = "",
  documentTypes = DOCUMENT_TYPES,
  showCondBlock,
  isDisabledCondBlock,
  toggleCondBlockModal,
  showVariableDropdown,
  variableList,
  handleDocumentChange,
}) {
  // local state for colors removed — we derive current colors from the editor

  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        isBold: ctx.editor.isActive("bold") ?? false,
        canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
        isItalic: ctx.editor.isActive("italic") ?? false,
        canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
        isUnderline: ctx.editor.isActive("underline") ?? false,
        canUnderline: ctx.editor.can().chain().toggleUnderline().run() ?? false,
        isStrike: ctx.editor.isActive("strike") ?? false,
        canStrike: ctx.editor.can().chain().toggleStrike().run() ?? false,
        isCode: ctx.editor.isActive("code") ?? false,
        canCode: ctx.editor.can().chain().toggleCode().run() ?? false,
        canClearMarks: ctx.editor.can().chain().unsetAllMarks().run() ?? false,
        isParagraph: ctx.editor.isActive("paragraph") ?? false,
        isHeading1: ctx.editor.isActive("heading", { level: 1 }) ?? false,
        isHeading2: ctx.editor.isActive("heading", { level: 2 }) ?? false,
        isHeading3: ctx.editor.isActive("heading", { level: 3 }) ?? false,
        isHeading4: ctx.editor.isActive("heading", { level: 4 }) ?? false,
        isHeading5: ctx.editor.isActive("heading", { level: 5 }) ?? false,
        isHeading6: ctx.editor.isActive("heading", { level: 6 }) ?? false,
        isBulletList: ctx.editor.isActive("bulletList") ?? false,
        isOrderedList: ctx.editor.isActive("orderedList") ?? false,
        isCodeBlock: ctx.editor.isActive("codeBlock") ?? false,
        isBlockquote: ctx.editor.isActive("blockquote") ?? false,
        currentTextColor: ctx.editor.getAttributes("textStyle")?.color ?? null,
        currentHighlightColor:
          ctx.editor.getAttributes("highlight")?.color ?? null,
        // canUndo: ctx.editor.can().chain().undo().run() ?? false,
        // canRedo: ctx.editor.can().chain().redo().run() ?? false,
      };
    },
  });

  const getCurrentHeading = () => {
    for (let level = 1; level <= 6; level++) {
      if (editor.isActive("heading", { level })) {
        return `Heading ${level}`;
      }
    }
    return "Normal";
  };

  const getCurrentAlignment = () => {
    if (editor.isActive({ textAlign: "center" })) {
      return <i className="bx bx-align-middle text-md"></i>;
    }
    if (editor.isActive({ textAlign: "right" })) {
      return <i className="bx bx-align-right text-md"></i>;
    }
    if (editor.isActive({ textAlign: "justify" })) {
      return <i className="bx bx-align-justify text-md"></i>;
    }
    return <i className="bx bx-align-left text-md"></i>;
  };

  // Function to handle file selection and insertion
  const handleMediaSelect = async (event) => {
    if (!editor) return;

    const file = event.target.files[0];
    const allowedTypes = [...IMAGE_TYPES, ...VIDEO_TYPES];
    const response = await uploadHandler(event, moduleType, allowedTypes);

    if (!response || !response.url) return;

    if (file.type.startsWith("video/")) {
      editor
        .chain()
        .focus()
        .insertVideo({
          src: response.url,
          controls: true,
          width: "100%",
        })
        .run();
    } else if (file.type.startsWith("image/")) {
      editor.chain().focus().setImage({ src: response.url, width: 320 }).run();
    }

    event.target.value = "";
  };

  const handleDocumentSelect = async (event) => {
    const response = await uploadHandler(event, moduleType, documentTypes);
    if (response && response.url) {
      handleDocumentChange({ ...response, type: FILE_TYPE.document });
    }
  };

  const insertField = (val) => {
    if (!editor) return;

    editor.chain().focus().insertContent(`{{${val}}}`).run();
  };

  // Render a button component
  const Button = ({ isSelected, title, ...props }) => {
    const baseStyle = {
      padding: "6px 10px",
      borderRadius: "6px",
      fontWeight: "600",
      cursor: "pointer",
      border: "0",
      width: "32px",
      height: "40px",
      backgroundColor: isSelected ? "#e5e7eb" : "#ffffff",
      color: isSelected ? "#030712" : "#333",
      transition: "all 0.2s ease",
    };
    return (
      <button {...props} style={baseStyle}>
        {title}
      </button>
    );
  };

  if (!editor) return null;

  return (
    <div className="d-flex gap-3 mt-3 p-2 rounded-top border">
      <div className="d-flex gap-0">
        <div className="dropdown">
          <button
            type="button"
            className="btn btn-light dropdown-toggle h-100 border-0 bg-transparent w-24 text-start"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            {getCurrentHeading()}
          </button>
          <ul className="dropdown-menu">
            <li>
              <button
                className="w-100 text-start border-0 bg-transparent"
                onClick={() => editor.chain().focus().setParagraph().run()}
              >
                Normal
              </button>
            </li>
            {[1, 2, 3, 4, 5, 6].map((level) => (
              <li key={level}>
                <button
                  className={`w-100 text-start border-0 bg-transparent font-bold ${
                    level === 1
                      ? "text-3xl"
                      : level === 2
                      ? "text-2xl"
                      : level === 3
                      ? "text-xl"
                      : level === 4
                      ? "text-lg"
                      : level === 5
                      ? "text-md"
                      : "text-base"
                  } ${editor.isActive("heading", { level }) ? "active" : ""}`}
                  onClick={() =>
                    editor.chain().focus().toggleHeading({ level }).run()
                  }
                >
                  Heading {level}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="dropdown">
          <button
            className="btn btn-light dropdown-toggle h-100 d-flex align-items-center border-0 bg-transparent"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            {getCurrentAlignment()}
          </button>
          <ul className="dropdown-menu min-w-unset w-14">
            <li>
              <button
                className={`w-100 text-center border-0 bg-transparent ${
                  editor.isActive({ textAlign: "left" }) ? "active" : ""
                }`}
                onClick={() =>
                  editor.chain().focus().setTextAlign("left").run()
                }
              >
                <i className="bx bx-align-left text-md"></i>
              </button>
            </li>
            <li>
              <button
                className={`w-100 text-center border-0 bg-transparent ${
                  editor.isActive({ textAlign: "center" }) ? "active" : ""
                }`}
                onClick={() =>
                  editor.chain().focus().setTextAlign("center").run()
                }
              >
                <i className="bx bx-align-middle text-md"></i>
              </button>
            </li>
            <li>
              <button
                className={`w-100 text-center border-0 bg-transparent ${
                  editor.isActive({ textAlign: "right" }) ? "active" : ""
                }`}
                onClick={() =>
                  editor.chain().focus().setTextAlign("right").run()
                }
              >
                <i className="bx bx-align-right text-md"></i>
              </button>
            </li>
            <li>
              <button
                className={`w-100 text-center border-0 bg-transparent ${
                  editor.isActive({ textAlign: "justify" }) ? "active" : ""
                }`}
                onClick={() =>
                  editor.chain().focus().setTextAlign("justify").run()
                }
              >
                <i className="bx bx-align-justify text-md"></i>
              </button>
            </li>
          </ul>
        </div>
        <div className="dropdown">
          <button
            className="btn btn-light dropdown-toggle h-100 d-flex align-items-center border-0 bg-transparent"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="bx bx-font-color text-lg"></i>
          </button>
          <ul className="dropdown-menu">
            <li className="px-3">
              <h3 className="text-sm font-medium">Background color</h3>
              <div className="color-grid mb-3">
                {COLORS.map((color) => {
                  const checked = editorState.currentHighlightColor === color;
                  return (
                    <label key={color} className="color-box">
                      <input
                        type="radio"
                        name="bg-color"
                        checked={checked}
                        onChange={() =>
                          editor.chain().focus().setHighlight({ color }).run()
                        }
                      />
                      <span
                        className="color-circle"
                        style={{ background: color }}
                      >
                        {checked && <span className="checkmark">✓</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
            </li>

            <li className="px-3">
              <h3 className="text-sm font-medium">Text color</h3>
              <div className="color-grid mb-0">
                {COLORS.map((color) => {
                  const checked = editorState.currentTextColor === color;
                  return (
                    <label key={color} className="color-box">
                      <input
                        type="radio"
                        name="text-color"
                        checked={checked}
                        onChange={() =>
                          editor.chain().focus().setColor(color).run()
                        }
                      />
                      <span
                        className="color-circle"
                        style={{ background: color }}
                      >
                        {checked && <span className="checkmark">✓</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
            </li>
          </ul>
        </div>
        {/* <label className="w-10 h-10 btn btn-light p-1 d-flex justify-content-center align-items-center border-0 bg-transparent">
          <input
            type="color"
            onChange={(e) =>
              editor.chain().focus().setColor(e.target.value).run()
            }
          />
        </label>
        <label className="w-10 h-10 btn btn-light p-1 d-flex justify-content-center align-items-center border-0 bg-transparent">
          <input
            type="color"
            onChange={(e) =>
              editor
                .chain()
                .focus()
                .setHighlight({ color: e.target.value })
                .run()
            }
          />
        </label> */}
      </div>
      <div className="d-flex gap-1 text-editor-format">
        <Button
          title="B"
          className={editorState.isBold ? "active" : ""}
          isSelected={editorState.isBold}
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editorState.canBold}
        />
        <Button
          title="I"
          className={editorState.isItalic ? "active" : ""}
          isSelected={editorState.isItalic}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editorState.canItalic}
        />
        <Button
          title="U"
          className={editorState.isUnderline ? "active" : ""}
          isSelected={editorState.isUnderline}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={!editorState.canUnderline}
        />
        <Button
          title="S"
          className={editorState.isStrike ? "active" : ""}
          isSelected={editorState.isStrike}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editorState.canStrike}
        />
        <Button
          title=""
          className={editorState.isCode ? "active" : ""}
          isSelected={editorState.isCode}
          onClick={() => editor.chain().focus().toggleCode().run()}
          disabled={!editorState.canCode}
        />
      </div>

      <div className="d-flex gap-1">
        <button
          className={`w-8 h-10 btn btn-light p-0 d-flex justify-content-center align-items-center border-0 ${
            editorState.isBulletList ? "active" : ""
          }`}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <i className="bx bx-list-ul text-xl"></i>
        </button>

        <button
          className={`w-8 h-10 btn btn-light p-0 d-flex justify-content-center align-items-center border-0 ${
            editorState.isOrderedList ? "active" : ""
          }`}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <i className="bx bx-list-ol text-xl"></i>
        </button>
        <button
          className={`h-10 px-2 btn btn-light d-flex justify-content-center align-items-center border-0 ${
            editorState.isCodeBlock ? "active" : ""
          }`}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        >
          <i className="bx bx-code text-lg"></i>
        </button>
        <button
          className={`w-8 h-10 btn btn-light p-0 d-flex justify-content-center align-items-center border-0 ${
            editorState.isBlockquote ? "active" : ""
          }`}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <i className="bx bxs-quote-left text-xl"></i>
        </button>
      </div>

      <div className="d-flex gap-1">
        {/* Image Upload */}
        <label className="w-8 h-10 btn btn-light p-0 d-flex justify-content-center align-items-center border-0 bg-transparent">
          <i className="bx bx-image-alt text-lg"></i>
          <input
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={(e) => handleMediaSelect(e, "image")}
          />
        </label>

        {/* Video Upload */}
        <label className="w-8 h-10 btn btn-light p-0 d-flex justify-content-center align-items-center border-0 bg-transparent">
          <i className="bx bx-video-recording text-lg"></i>
          {/* 🎥 */}
          <input
            type="file"
            accept="video/*"
            multiple
            style={{ display: "none" }}
            onChange={(e) => handleMediaSelect(e, "video")}
          />
        </label>

        <label className="w-8 h-10 btn btn-light p-0 d-flex justify-content-center align-items-center border-0 bg-transparent">
          <i className="bx bx-file text-lg"></i>
          {/* 📄 */}
          <input
            type="file"
            // accept=".pdf,.doc,.docx"
            multiple
            style={{ display: "none" }}
            onChange={(e) => handleDocumentSelect(e, "document")}
          />
        </label>
      </div>

      {showVariableDropdown && (
        <div className="dropdown">
          <button
            className="btn btn-light dropdown-toggle h-100"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            disabled={variableList.length === 0}
          >
            Select field
          </button>

          <ul className="dropdown-menu max-h-200 overflow-y-auto">
            {variableList.length === 0 ? (
              <li>
                <span className="dropdown-item-text text-muted">
                  No Data Available
                </span>
              </li>
            ) : (
              variableList.map((item, idx) => (
                <li key={`${item}-${idx}`}>
                  <button
                    className="dropdown-item"
                    onClick={() => insertField(item)}
                  >
                    {item}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}

      {showCondBlock && (
        <button
          className="btn btn-outline-primary-light font-medium"
          onClick={toggleCondBlockModal}
          disabled={isDisabledCondBlock}
        >
          <i className="bx bx-git-merge"></i> Add Condition
        </button>
      )}
    </div>
  );
}
